import { markRaw } from 'vue'
import OwnerNameColumn from './OwnerNameColumn.vue'
import OwnerEmailColumn from './OwnerEmailColumn.vue'
import OwnerPhoneColumn from './OwnerPhoneColumn.vue'
import OwnerAssignedAtColumn from './OwnerAssignedAtColumn.vue'
import OwnerStaffingAgencyColumn from './OwnerStaffingAgencyColumn.vue'
import OwnerResponsibilityColumn from './OwnerResponsibilityColumn.vue'
import {
  OwnerAssignedAtColumnFragment,
  OwnerAssignedAtColumnFragmentDoc,
  OwnerEmailColumnFragment,
  OwnerEmailColumnFragmentDoc,
  OwnerNameColumnFragment,
  OwnerNameColumnFragmentDoc,
  OwnerPhoneColumnFragment,
  OwnerPhoneColumnFragmentDoc,
  OwnerResponsibilityColumnFragment,
  OwnerResponsibilityColumnFragmentDoc,
  OwnerStaffingAgencyColumnFragment,
  OwnerStaffingAgencyColumnFragmentDoc,
  ClientRelationOwnerOrderByColumn,
  ClientRelationAgencyOwnerOrderByColumn,
  UserEmailColumnFragment,
  UserNameColumnFragment,
  UserPhoneColumnFragment,
} from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { ColumnMaps } from '@/components/GraphqlDataTable/columns/types'
import { sortPair } from '@/components/GraphqlDataTable/columns/sortPair'
import { formatPhone } from '@/utils/phone'
import { responsibilityLabel } from '@/components/Owners/responsibilities'

// Nested user fragments are masked in the generated row type, so the describers read them as plain objects.
const ownerUser = (row: { user: unknown }) =>
  row.user as UserNameColumnFragment & UserEmailColumnFragment & UserPhoneColumnFragment

const columns: ColumnMaps = {
  OwnerNameColumn: {
    fragment: OwnerNameColumnFragmentDoc,
    component: markRaw(OwnerNameColumn),
    columnDef: {
      title: $t('Name'),
      key: 'OwnerNameColumn',
      orderAble: true,
      renderUsing: {
        component: markRaw(OwnerNameColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      { title: 'Name', extraction: (row: OwnerNameColumnFragment) => ownerUser(row).name },
    ],
    sort: sortPair(ClientRelationOwnerOrderByColumn.Name, $t('Name'), $t('A - Z'), $t('Z - A')),
  },
  OwnerEmailColumn: {
    fragment: OwnerEmailColumnFragmentDoc,
    component: markRaw(OwnerEmailColumn),
    columnDef: {
      title: $t('Email'),
      key: 'OwnerEmailColumn',
      renderUsing: {
        component: markRaw(OwnerEmailColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      { title: 'Email', extraction: (row: OwnerEmailColumnFragment) => ownerUser(row).email ?? '' },
    ],
  },
  OwnerPhoneColumn: {
    fragment: OwnerPhoneColumnFragmentDoc,
    component: markRaw(OwnerPhoneColumn),
    columnDef: {
      title: $t('Phone'),
      key: 'OwnerPhoneColumn',
      renderUsing: {
        component: markRaw(OwnerPhoneColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Phone',
        extraction: (row: OwnerPhoneColumnFragment) => formatPhone(ownerUser(row).phone ?? ''),
      },
    ],
  },
  OwnerAssignedAtColumn: {
    fragment: OwnerAssignedAtColumnFragmentDoc,
    component: markRaw(OwnerAssignedAtColumn),
    columnDef: {
      title: $t('Assigned'),
      key: 'OwnerAssignedAtColumn',
      orderAble: true,
      renderUsing: {
        component: markRaw(OwnerAssignedAtColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Assigned',
        extraction: (row: OwnerAssignedAtColumnFragment) => row.assignedAt ?? '',
      },
    ],
    sort: sortPair(
      ClientRelationOwnerOrderByColumn.AssignedAt,
      $t('Assigned'),
      $t('Oldest first'),
      $t('Newest first'),
    ),
  },
  OwnerStaffingAgencyColumn: {
    fragment: OwnerStaffingAgencyColumnFragmentDoc,
    component: markRaw(OwnerStaffingAgencyColumn),
    columnDef: {
      title: $t('Staffing agency'),
      key: 'OwnerStaffingAgencyColumn',
      orderAble: true,
      ignoreClickForRowSelection: true,
      renderUsing: {
        component: markRaw(OwnerStaffingAgencyColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Staffing agency',
        extraction: (row: OwnerStaffingAgencyColumnFragment) =>
          (row.owned as { supplier?: { name?: string } } | null)?.supplier?.name ?? '',
      },
    ],
    sort: sortPair(
      ClientRelationAgencyOwnerOrderByColumn.StaffingAgency,
      $t('Staffing agency'),
      $t('A - Z'),
      $t('Z - A'),
    ),
  },
  OwnerResponsibilityColumn: {
    fragment: OwnerResponsibilityColumnFragmentDoc,
    component: markRaw(OwnerResponsibilityColumn),
    columnDef: {
      title: $t('Responsibility'),
      key: 'OwnerResponsibilityColumn',
      renderUsing: {
        component: markRaw(OwnerResponsibilityColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Responsibility',
        extraction: (row: OwnerResponsibilityColumnFragment) =>
          row.responsibility ? responsibilityLabel(row.responsibility) : '',
      },
    ],
  },
}

export default columns
